/**
 * Clerk Search Overlay - Two-State Progressive Search
 * State 1: Collapsed search bar
 * State 2: Expanded full-screen with results
 */

(function() {
  'use strict';

  const selectors = {
    overlay: '[data-clerk-search-overlay]',
    trigger: '[data-clerk-search-trigger]',
    input: '[data-clerk-search-input]',
    backdrop: '[data-backdrop]',
    close: '[data-close]',
    content: '[data-content]',
    resultsGrid: '[data-results-grid]',
    noResults: '[data-no-results]',
    barInner: '.clerk-search-overlay__bar-inner'
  };

  const classes = {
    open: 'clerk-search-overlay--open',
    expanded: 'clerk-search-overlay--expanded',
    bodyLocked: 'clerk-search-open',
    barActive: 'clerk-search-overlay__bar-inner--active'
  };

  class ClerkSearchOverlay {
    constructor() {
      this.overlay = document.querySelector(selectors.overlay);
      if (!this.overlay) return;

      this.triggers = document.querySelectorAll(selectors.trigger);
      this.input = this.overlay.querySelector(selectors.input);
      this.backdrop = this.overlay.querySelector(selectors.backdrop);
      this.closeBtn = this.overlay.querySelector(selectors.close);
      this.content = this.overlay.querySelector(selectors.content);
      this.resultsGrid = this.overlay.querySelector(selectors.resultsGrid);
      this.noResults = this.overlay.querySelector(selectors.noResults);
      this.barInner = this.overlay.querySelector(selectors.barInner);

      this.cachedResults = {};
      this.debounceTimer = null;
      this.isOpen = false;
      this.isExpanded = false;

      this.init();
    }

    init() {
      this.setupEventListeners();
    }

    setupEventListeners() {
      // Trigger buttons (open overlay)
      this.triggers.forEach(trigger => {
        trigger.addEventListener('click', () => this.open());
      });

      // Close button
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      // Backdrop click (close)
      if (this.backdrop) {
        this.backdrop.addEventListener('click', () => this.close());
      }

      // Escape key (close)
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });

      // Search input (debounced)
      if (this.input) {
        this.input.addEventListener('input', () => {
          // Toggle active state on bar-inner based on input value
          const hasText = this.input.value.trim().length > 0;
          if (this.barInner) {
            if (hasText) {
              this.barInner.classList.add(classes.barActive);
            } else {
              this.barInner.classList.remove(classes.barActive);
            }
          }

          clearTimeout(this.debounceTimer);
          this.debounceTimer = setTimeout(() => {
            this.handleSearch();
          }, 300);
        });

        // Enter key submits to search page
        this.input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            const query = this.input.value.trim();
            if (query) {
              window.location.href = `/search?q=${encodeURIComponent(query)}`;
            }
          }
        });
      }
    }

    open() {
      this.overlay.classList.add(classes.open);
      document.body.classList.add(classes.bodyLocked);
      this.isOpen = true;

      // Focus input immediately (needed for iOS to show keyboard)
      if (this.input) {
        this.input.focus();
        // Also try after transition in case immediate focus didn't work
        setTimeout(() => {
          this.input.focus();
        }, 150);
      }
    }

    close() {
      this.overlay.classList.remove(classes.open, classes.expanded);
      document.body.classList.remove(classes.bodyLocked);
      this.isOpen = false;
      this.isExpanded = false;

      // Clear input and results
      if (this.input) {
        this.input.value = '';
      }

      // Remove active state from bar
      if (this.barInner) {
        this.barInner.classList.remove(classes.barActive);
      }

      this.hideResults();
    }

    collapse() {
      this.overlay.classList.remove(classes.expanded);
      this.isExpanded = false;
    }

    expand() {
      this.overlay.classList.add(classes.expanded);
      this.isExpanded = true;
    }

    async handleSearch() {
      const query = this.input.value.trim();

      if (!query) {
        this.collapse();
        this.hideResults();
        return;
      }

      // Check cache
      if (this.cachedResults[query]) {
        this.renderResults(this.cachedResults[query], query);
        return;
      }

      // Call Clerk API
      await this.searchClerk(query);
    }

    async searchClerk(query) {
      if (typeof window.Clerk !== 'function') {
        console.warn('[Clerk Search] Clerk SDK not loaded, falling back to Shopify search');
        this.fallbackToShopifySearch(query);
        return;
      }

      const config = {
        key: typeof theme !== 'undefined' && theme.clerk && theme.clerk.apiKey ? theme.clerk.apiKey : '',
        limit: 12,
        labels: ['Predictive Search'],
        query: query,
        attributes: ['id', 'url', 'name', 'price', 'list_price', 'on_sale', 'images', 'variants']
      };

      if (!config.key) {
        console.warn('[Clerk Search] No API key, falling back to Shopify search');
        this.fallbackToShopifySearch(query);
        return;
      }

      // Call Clerk
      window.Clerk(
        'call',
        'search/predictive',
        config,
        (response) => {
          this.handleClerkResponse(response, query);
        },
        (error) => {
          console.error('[Clerk Search] API error:', error);
          this.fallbackToShopifySearch(query);
        }
      );
    }

    async handleClerkResponse(response, query) {
      const products = response.product_data || [];

      if (products.length === 0) {
        this.showNoResults(query);
        this.expand();
        return;
      }

      // Extract handles from Clerk URLs
      const productHandles = products.map(product => {
        if (product.url) {
          const urlParts = product.url.split('/products/');
          return urlParts[1] || null;
        }
        return null;
      }).filter(handle => handle !== null);

      if (productHandles.length === 0) {
        this.showNoResults(query);
        this.expand();
        return;
      }

      try {
        // Fetch product cards via Section Rendering API (parallel)
        const renderPromises = productHandles.map((handle, index) =>
          this.fetchProductCard(handle, products[index].id)
        );

        const productCards = await Promise.all(renderPromises);

        // Filter out nulls
        const validCards = productCards.filter(card => card !== null);

        if (validCards.length === 0) {
          this.showNoResults(query);
          this.expand();
          return;
        }

        // Cache results
        this.cachedResults[query] = {
          cards: validCards,
          count: validCards.length
        };

        // Render results (swatches initialized inside renderResults)
        this.renderResults(this.cachedResults[query], query);

        // Track clicks via Clerk logging API (without intercepting navigation)
        this.registerClickTracking(query);

      } catch (error) {
        console.error('[Clerk Search] Error fetching product cards:', error);
        this.fallbackToShopifySearch(query);
      }
    }

    async fetchProductCard(productHandle, productId) {
      try {
        const url = `/products/${productHandle}?section_id=api-product-card`;
        const response = await fetch(url);

        if (!response.ok) {
          console.warn(`[Clerk Search] Failed to fetch card for ${productHandle}`);
          return null;
        }

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const productCard = doc.querySelector('[data-grid-item]');

        if (productCard) {
          // Add Clerk product ID for click tracking
          productCard.setAttribute('data-clerk-product-id', productId);
          return productCard.outerHTML;
        }

        return null;
      } catch (error) {
        console.error(`[Clerk Search] Error fetching card for ${productHandle}:`, error);
        return null;
      }
    }

    renderResults(data, query) {
      // Clear grid
      this.resultsGrid.innerHTML = '';

      // Insert product cards
      data.cards.forEach(cardHTML => {
        this.resultsGrid.insertAdjacentHTML('beforeend', cardHTML);
      });

      // Hide no results
      if (this.noResults) {
        this.noResults.style.display = 'none';
      }

      // Register new product cards with deferred-media-loader
      document.dispatchEvent(new CustomEvent('load-more:complete'));

      // Initialize swatches (important for both fresh and cached results)
      if (typeof window.initLinkedProducts === 'function') {
        setTimeout(() => {
          window.initLinkedProducts();
        }, 150);
      }

      // Expand to show results
      this.expand();
    }

    showNoResults(query) {
      // Clear grid
      this.resultsGrid.innerHTML = '';

      // Show no results message
      if (this.noResults) {
        this.noResults.style.display = 'block';
        const heading = this.noResults.querySelector('.ds-h03');
        if (heading) {
          heading.textContent = `No results for "${query}"`;
        }
      }
    }

    hideResults() {
      if (this.resultsGrid) {
        this.resultsGrid.innerHTML = '';
      }

      if (this.noResults) {
        this.noResults.style.display = 'none';
      }
    }

    registerClickTracking(query) {
      if (typeof window.Clerk !== 'function') return;

      const apiKey = typeof theme !== 'undefined' && theme.clerk && theme.clerk.apiKey ? theme.clerk.apiKey : '';
      if (!apiKey) return;

      this.resultsGrid.querySelectorAll('[data-clerk-product-id]').forEach((card) => {
        card.addEventListener('click', () => {
          const productId = card.getAttribute('data-clerk-product-id');
          if (productId) {
            window.Clerk('call', 'log/click', {
              key: apiKey,
              product: productId,
              query: query,
              labels: ['Predictive Search'],
            });
          }
        });
      });
    }

    async fallbackToShopifySearch(query) {
      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=12&section_id=predictive-search`;
        const response = await fetch(url);

        if (!response.ok) {
          this.showNoResults(query);
          this.expand();
          return;
        }

        const data = await response.json();
        const products = data.resources?.results?.products || [];

        if (products.length === 0) {
          this.showNoResults(query);
          this.expand();
          return;
        }

        // Fetch product cards for Shopify results
        const renderPromises = products.map(product =>
          this.fetchProductCard(product.handle, product.id)
        );

        const productCards = await Promise.all(renderPromises);
        const validCards = productCards.filter(card => card !== null);

        if (validCards.length === 0) {
          this.showNoResults(query);
          this.expand();
          return;
        }

        // Cache and render (swatches initialized inside renderResults)
        this.cachedResults[query] = {
          cards: validCards,
          count: validCards.length
        };

        this.renderResults(this.cachedResults[query], query);

      } catch (error) {
        console.error('[Clerk Search] Fallback error:', error);
        this.showNoResults(query);
        this.expand();
      }
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new ClerkSearchOverlay();
    });
  } else {
    new ClerkSearchOverlay();
  }
})();
